import React, { useState, useEffect, useRef } from "react";
import Nav from "./Nav";
import Footer from "./Footer";
import "./ThankYouScreen.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function buildMemberEntries(purchases) {
  const entries = [];
  if (!purchases) return entries;
  purchases.forEach((plan) => {
    for (let i = 1; i <= plan.qty; i++) {
      entries.push({
        planId: plan.id,
        planTitle: plan.title,
        type: plan.type,
        memberLabel:
          plan.type === "primary"
            ? `Primary Plan Member ${i}`
            : `${plan.title} — Member ${i}`,
        name: "",
        email: "",
        relation: "",
        mobile: "",
        saved: false,
      });
    }
  });
  return entries;
}

export default function ThankYouScreen({ open, orderData, onReset }) {
  const [memberEntries, setMemberEntries] = useState([]);
  const [openFormPlanId, setOpenFormPlanId] = useState(null);
  const [formState, setFormState] = useState({ name: "", email: "", relation: "", mobile: "" });
  const [formErrors, setFormErrors] = useState({ name: false, email: false, mobile: false, dup: false });
  const [otpSent, setOtpSent] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(null);
  const [memberPlanDetailsId, setMemberPlanDetailsId] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [activationStarted, setActivationStarted] = useState(false);
  const [activationStates, setActivationStates] = useState({});
  const [showActivation, setShowActivation] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [activationApiLoading, setActivationApiLoading] = useState(false);

  const otpRefs = useRef([]);
  const activationRef = useRef(null);
  const completionRef = useRef(null);

  const mapPlanIdToPurchaseId = (planId, purchaseList) => {
    const hit = (purchaseList || []).find(
      (p) => p?.sku === planId || p?.plan_id === planId || p?.id === planId,
    );
    return hit?.id || null;
  };

  // Reset all state when a fresh order comes in
  useEffect(() => {
    if (orderData) {
      setMemberEntries(buildMemberEntries(orderData.purchases));
      setOpenFormPlanId(null);
      setFormState({ name: "", email: "", relation: "", mobile: "" });
      setFormErrors({ name: false, email: false, mobile: false, dup: false });
      setOtpSent(false);
      setOtpValues(["", "", "", "", "", ""]);
      setOtpError(false);
      setOtpVerified(false);
      setIsExistingUser(null);
      setMemberPlanDetailsId("");
      setSendingOtp(false);
      setVerifyingOtp(false);
      setOtpCooldown(0);
      setActivationStarted(false);
      setActivationStates({});
      setShowActivation(false);
      setShowCompletion(false);
      setActivationApiLoading(false);
    }
  }, [orderData]);

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = setInterval(() => {
      setOtpCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpCooldown]);

  // Prefill already added members from backend on first load
  useEffect(() => {
    if (!orderData || !API_BASE_URL) return;
    const memberId = orderData?.member_details_id;
    const paymentId = orderData?.payment_request_id;
    if (!memberId || !paymentId) return;

    let isCancelled = false;

    async function fetchExistingMemberPlanData() {
      const requestOptions = {
        method: "GET",
        redirect: "follow",
      };

      try {
        const response = await fetch(
          `${API_BASE_URL}/member_plan_details_data?member_id=${encodeURIComponent(
            String(memberId),
          )}&payment_id=${encodeURIComponent(String(paymentId))}`,
          requestOptions,
        );
        const text = await response.text();
        const result = JSON.parse(text);
        const status = result?.data?.status;
        const existing = result?.data?.data?.member_plan_data || [];
        if (status !== "success" || !Array.isArray(existing) || isCancelled) return;

        const next = buildMemberEntries(orderData?.purchases);
        const matchedActivationFlags = [];
        existing.forEach((entry) => {
          const matchedPurchaseId = mapPlanIdToPurchaseId(entry?.plan_id, orderData?.purchases);
          if (!matchedPurchaseId) return;

          const slotIdx = next.findIndex(
            (e) => e.planId === matchedPurchaseId && !e.saved,
          );
          if (slotIdx === -1) return;

          next[slotIdx] = {
            ...next[slotIdx],
            name: entry?.name || "",
            email: entry?.email || "",
            mobile: entry?.mobile || "",
            relation: entry?.relation || "",
            saved: true,
          };
          matchedActivationFlags.push(Number(entry?.plan_activated) === 1);
        });
        setMemberEntries(next);

        const allMemberDetailsFilled = next.length > 0 && next.every((e) => e.saved);
        if (!allMemberDetailsFilled) return;

        const allActivated =
          matchedActivationFlags.length === next.length &&
          matchedActivationFlags.every(Boolean);

        if (allActivated) {
          const completedStates = {};
          next.forEach((_, i) => {
            completedStates[i] = "active";
          });
          setActivationStarted(true);
          setActivationStates(completedStates);
          setShowActivation(true);
          setShowCompletion(true);
          return;
        }

        if (!activationStarted) {
          setActivationStarted(true);
          triggerActivation(next);
        }
      } catch (error) {
        console.error("member_plan_details_data API error", error);
      }
    }

    fetchExistingMemberPlanData();
    return () => {
      isCancelled = true;
    };
  }, [orderData, activationStarted]);

  // ── Helpers (must be above early return so hooks order is stable) ──────────

  const getNextEmptyIndex = (entries, planId) =>
    entries.findIndex((e) => e.planId === planId && !e.saved);

  const savedMobiles = memberEntries.filter((e) => e.saved).map((e) => e.mobile);

  const { name: _n, email: _e, relation: _r, mobile: _m } = formState;
  const _validName = _n.trim().length >= 3 && _n.trim().length <= 30;
  const _validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(_e.trim());
  const _validMobile = /^[6-9]\d{9}$/.test(_m);
  const _dup = savedMobiles.includes(_m);
  const _hasSlot = getNextEmptyIndex(memberEntries, openFormPlanId) !== -1;
  const isFormReady = _validName && _validEmail && _validMobile && !_dup && _r && _hasSlot;

  if (!orderData) return null;

  const purchases = orderData.purchases || [];

  // ── Form handlers ─────────────────────────────────────────────────────────

  const handleOpenForm = (planId) => {
    setOpenFormPlanId(planId);
    setFormState({ name: "", email: "", relation: "", mobile: "" });
    setFormErrors({ name: false, email: false, mobile: false, dup: false });
    setOtpSent(false);
    setOtpValues(["", "", "", "", "", ""]);
    setOtpError(false);
    setOtpVerified(false);
    setIsExistingUser(null);
    setMemberPlanDetailsId("");
    setSendingOtp(false);
    setVerifyingOtp(false);
    setOtpCooldown(0);
  };

  const handleCloseForm = () => setOpenFormPlanId(null);

  const handleFormChange = (field, value) => {
    if (field === "mobile") value = value.replace(/\D/g, "").slice(0, 10);
    setFormState((prev) => ({ ...prev, [field]: value }));

    // Live error hints
    if (field === "name") {
      const v = value.trim();
      setFormErrors((prev) => ({
        ...prev,
        name: v.length > 0 && (v.length < 3 || v.length > 30),
      }));
    }
    if (field === "email") {
      const v = value.trim();
      setFormErrors((prev) => ({
        ...prev,
        email: v.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
      }));
    }
    if (field === "mobile") {
      const validMobile = /^[6-9]\d{9}$/.test(value);
      const dup = savedMobiles.includes(value);
      setFormErrors((prev) => ({
        ...prev,
        mobile: value.length > 0 && !validMobile,
        dup: validMobile && dup,
      }));
    }

    // Reset OTP state on field edit
    if (otpSent || otpVerified) {
      setOtpSent(false);
      setOtpVerified(false);
      setIsExistingUser(null);
      setOtpValues(["", "", "", "", "", ""]);
      setOtpError(false);
      setMemberPlanDetailsId("");
    }
  };

  const handleSendOtp = async () => {
    if (!API_BASE_URL) return;
    if (otpCooldown > 0) return;

    const selectedPlan = purchases.find((item) => item.id === openFormPlanId);
    const memberId = orderData?.member_details_id || "";
    const paymentId = orderData?.payment_request_id || "";
    const planId = selectedPlan?.sku || selectedPlan?.plan_id || "";
    if (!selectedPlan || !memberId || !paymentId || !planId) {
      setOtpError(true);
      return;
    }

    setSendingOtp(true);
    setOtpError(false);
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      member_id: String(memberId),
      plan_id: String(planId),
      name: formState.name,
      email: formState.email,
      amount: selectedPlan.base_plan_amount,
      gst_amount: selectedPlan.base_plan_gst_amount,
      payment_id: String(paymentId),
      mobile: formState.mobile,
      relation: String(formState.relation || "").toLowerCase(),
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(`${API_BASE_URL}/send_otp`, requestOptions);
      const text = await response.text();
      const result = JSON.parse(text);
      const status = result?.data?.status;
      if (status !== "success") {
        setOtpError(true);
        return;
      }

      const planDetailsId = result?.data?.data?.member_plan_details_id;
      if (!planDetailsId) {
        setOtpError(true);
        return;
      }
      setMemberPlanDetailsId(String(planDetailsId));
      setOtpSent(true);
      setOtpValues(["", "", "", "", "", ""]);
      setOtpCooldown(30);
      setTimeout(() => otpRefs.current[0]?.focus(), 80);
    } catch (error) {
      console.error("send_otp API error", error);
      setOtpError(true);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleOtpChange = (idx, val) => {
    val = val.replace(/\D/g, "").slice(0, 1);
    const next = [...otpValues];
    next[idx] = val;
    setOtpValues(next);
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otpValues[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otp = otpValues.join("");
    if (otp.length !== 6 || !memberPlanDetailsId || !API_BASE_URL) {
      setOtpError(true);
      return;
    }

    setVerifyingOtp(true);
    setOtpSent(true);
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      id: String(memberPlanDetailsId),
      mobile: formState.mobile,
      otp,
    });
    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(`${API_BASE_URL}/verify_otp`, requestOptions);
      const text = await response.text();
      const result = JSON.parse(text);
      if (result?.data?.status !== "success") {
        setOtpError(true);
        return;
      }

      setOtpError(false);
      setOtpSent(false);
      setOtpVerified(true);
      const mobile = formState.mobile;
      setIsExistingUser(parseInt(mobile[mobile.length - 1], 10) % 2 === 0);
    } catch (error) {
      console.error("verify_otp API error", error);
      setOtpError(true);
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleSaveMember = () => {
    const idx = getNextEmptyIndex(memberEntries, openFormPlanId);
    if (idx === -1) return;

    const updated = [...memberEntries];
    updated[idx] = {
      ...updated[idx],
      name: formState.name.trim(),
      email: formState.email.trim(),
      relation: formState.relation,
      mobile: formState.mobile,
      saved: true,
    };
    setMemberEntries(updated);

    // Reset form
    setFormState({ name: "", email: "", relation: "", mobile: "" });
    setFormErrors({ name: false, email: false, mobile: false, dup: false });
    setOtpSent(false);
    setOtpValues(["", "", "", "", "", ""]);
    setOtpError(false);
    setOtpVerified(false);
    setIsExistingUser(null);
    setMemberPlanDetailsId("");
    setSendingOtp(false);
    setVerifyingOtp(false);
    setOtpCooldown(0);

    // Close form if no more unsaved slots for this plan
    const nextEmpty = getNextEmptyIndex(updated, openFormPlanId);
    if (nextEmpty === -1) setOpenFormPlanId(null);

    // Check if every member across all plans is saved → kick off activation
    const allSaved = updated.every((e) => e.saved);
    if (allSaved && !activationStarted) {
      setActivationStarted(true);
      triggerActivation(updated);
    }
  };

  const activateMemberPlans = async () => {
    if (!API_BASE_URL) return false;
    const paymentId = orderData?.payment_request_id;
    const memberId = orderData?.member_details_id;
    if (!paymentId || !memberId) return false;

    setActivationApiLoading(true);
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      payment_id: String(paymentId),
      member_id: String(memberId),
    });
    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(`${API_BASE_URL}/member_plan_activate`, requestOptions);
      const text = await response.text();
      const result = JSON.parse(text);
      return result?.data?.status === "success";
    } catch (error) {
      console.error("member_plan_activate API error", error);
      return false;
    } finally {
      setActivationApiLoading(false);
    }
  };

  // ── Activation sequence ────────────────────────────────────────────────────

  const triggerActivation = async (entries) => {
    // Show activation card immediately so loader is visible while API is in-flight.
    setShowActivation(true);
    const pendingStates = {};
    entries.forEach((_, i) => {
      pendingStates[i] = "pending";
    });
    setActivationStates(pendingStates);
    setTimeout(() => activationRef.current?.scrollIntoView({ behavior: "smooth" }), 120);

    await activateMemberPlans();
    const initial = {};
    entries.forEach((_, i) => {
      initial[i] = i === 0 ? "progress" : "pending";
    });
    setActivationStates(initial);

    entries.forEach((_, i) => {
      setTimeout(() => {
        setActivationStates((prev) => {
          const next = { ...prev, [i]: "active" };
          if (i + 1 < entries.length) next[i + 1] = "progress";
          return next;
        });

        if (i === entries.length - 1) {
          setTimeout(() => {
            setShowCompletion(true);
            setTimeout(
              () => completionRef.current?.scrollIntoView({ behavior: "smooth" }),
              120,
            );
          }, 800);
        }
      }, (i + 1) * 1200);
    });
  };

  // ── Derived data ───────────────────────────────────────────────────────────

  const entriesByPlan = {};
  memberEntries.forEach((e) => {
    if (!entriesByPlan[e.planId]) entriesByPlan[e.planId] = [];
    entriesByPlan[e.planId].push(e);
  });

  const isAllSaved = (planId) => {
    const arr = entriesByPlan[planId] || [];
    return arr.length > 0 && arr.every((e) => e.saved);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={`ty-screen pf-screen${open ? " open" : ""}`}>

      {/* ── Nav — sticky inside the fixed overlay, spans full width ── */}
      <Nav rightSlot={
        <span className="pf-status-pill">
          <span className="pf-status-dot" />
          Payment Successful
        </span>
      } />

      <div className="pf-page">

        {/* ── Flow header: hero + summary ── */}
        <div className="pf-flow-header">

          {/* Hero */}
          <div className="pf-card pf-hero">
            <div className="pf-success-badge">
              <div className="pf-check-icon">✓</div>
              Order Confirmed
            </div>
            <h1 className="pf-hero-title">Thank You, {orderData.name}</h1>
            <p className="pf-hero-order">
              Order Number: <strong>{orderData.orderId}</strong>
            </p>
            <p className="pf-hero-sub">
              Your payment has been completed successfully. Please activate the plan(s).
            </p>
            <div className="pf-notice-box">
              <div>⚠️</div>
              <div>
                <strong>Next step required</strong>
                <p>Add member details below to activate the plan(s).</p>
              </div>
            </div>
          </div>

          {/* Summary + per-plan forms */}
          <div className="pf-card pf-summary">
            <div className="pf-summary-heading">Plan(s) Purchased</div>

            {purchases.map((item) => {
              const planEntries = entriesByPlan[item.id] || [];
              const allDone = isAllSaved(item.id);
              const isOpen = openFormPlanId === item.id;
              const planTitleWithQty =
                Number(item?.qty) > 1 ? `${item.title} x ${item.qty}` : item.title;

              return (
                <div key={item.id} className="pf-summary-group">
                  <div className="pf-summary-label">
                    {item.type === "primary" ? "Primary Plan" : "Add-On Plan"}
                  </div>
                  <div className="pf-summary-value">{planTitleWithQty}</div>

                  {/* CTA */}
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 12 }}>
                    {allDone ? (
                      <span className="pf-all-added-badge">✓ All members added</span>
                    ) : (
                      <button
                        className="pf-btn pf-btn-primary"
                        style={{ padding: "10px 14px", fontSize: 13 }}
                        onClick={() => handleOpenForm(item.id)}
                      >
                        Activate Plan
                      </button>
                    )}
                  </div>

                  {/* Saved member chips */}
                  {planEntries.some((m) => m.saved) && (
                    <div className="pf-member-list">
                      {planEntries.filter((m) => m.saved).map((m, i) => (
                        <div key={i} className="pf-member-row">
                          <div>
                            <h4>{m.name}</h4>
                            <p>{m.relation ? `${m.relation} · ` : ""}{m.mobile}</p>
                          </div>
                          <div className="pf-tag-added">✓ Added</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Inline member form */}
                  {isOpen && (
                    <div className="pf-form-wrap">
                      <div className="pf-form-card">
                        <div className="pf-form-header">
                          <div>
                            <h3>{item.type === "primary" ? "Add Primary Member" : "Add Member"}</h3>
                            <p>{planTitleWithQty}</p>
                          </div>
                          <button className="pf-btn pf-btn-secondary" onClick={handleCloseForm}>
                            Close
                          </button>
                        </div>

                        <div className="pf-form-body">
                          {/* Fields */}
                          <div className="pf-inline-grid">
                            <div className="pf-field">
                              <label>Full Name</label>
                              <input
                                type="text"
                                placeholder="e.g. Priya Sharma"
                                value={formState.name}
                                onChange={(e) => handleFormChange("name", e.target.value)}
                              />
                            </div>
                            <div className="pf-field">
                              <label>Relation</label>
                              <select
                                value={formState.relation}
                                onChange={(e) => handleFormChange("relation", e.target.value)}
                              >
                                <option value="">Select</option>
                                <option>Self</option>
                                <option>Spouse</option>
                                <option>Child</option>
                                <option>Parent</option>
                                <option>Others</option>
                              </select>
                            </div>
                            <div className="pf-field">
                              <label>Email</label>
                              <input
                                type="email"
                                placeholder="example@mail.com"
                                value={formState.email}
                                onChange={(e) => handleFormChange("email", e.target.value)}
                              />
                            </div>
                            <div className="pf-field">
                              <label>Mobile Number</label>
                              <input
                                type="text"
                                maxLength={10}
                                placeholder="10-digit number"
                                value={formState.mobile}
                                onChange={(e) => handleFormChange("mobile", e.target.value)}
                              />
                            </div>
                          </div>

                          {/* Errors */}
                          {formErrors.name && (
                            <div className="pf-error-text">
                              Please enter a valid name between 3 and 30 characters.
                            </div>
                          )}
                          {formErrors.mobile && (
                            <div className="pf-error-text">
                              Please enter a valid 10-digit mobile number.
                            </div>
                          )}
                          {formErrors.email && (
                            <div className="pf-error-text">
                              Please enter a valid email address.
                            </div>
                          )}
                          {formErrors.dup && (
                            <div className="pf-error-text">
                              This mobile number is already assigned to another covered member.
                            </div>
                          )}

                          {/* Send OTP */}
                          <div className="pf-form-actions">
                            <button
                              className="pf-btn pf-btn-primary"
                              onClick={handleSendOtp}
                              disabled={!isFormReady || otpVerified || sendingOtp || otpCooldown > 0}
                            >
                              {otpVerified
                                ? "OTP Verified ✓"
                                : sendingOtp
                                ? "Sending OTP..."
                                : otpCooldown > 0
                                ? `Resend in ${otpCooldown}s`
                                : "Send OTP"}
                            </button>
                          </div>

                          {/* OTP entry */}
                          {otpSent && (
                            <div className="pf-otp-panel">
                              <div className="pf-otp-panel-title">Verify Mobile Number</div>
                              <div className="pf-otp-panel-sub">
                                OTP sent to <strong>+91 {formState.mobile}</strong>
                              </div>
                              <div className="pf-otp-row">
                                {[0, 1, 2, 3, 4, 5].map((idx) => (
                                  <input
                                    key={idx}
                                    className="pf-otp-input"
                                    maxLength={1}
                                    ref={(el) => (otpRefs.current[idx] = el)}
                                    value={otpValues[idx]}
                                    onChange={(e) => handleOtpChange(idx, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                                  />
                                ))}
                              </div>
                              {otpError && (
                                <div className="pf-otp-error">
                                  Invalid OTP or OTP verification failed. Please try again.
                                </div>
                              )}
                              <div className="pf-form-actions">
                                <button
                                  className="pf-btn pf-btn-primary"
                                  onClick={handleVerifyOtp}
                                  disabled={verifyingOtp}
                                >
                                  {verifyingOtp ? "Verifying..." : "Verify OTP"}
                                </button>
                                <button
                                  className="pf-btn pf-btn-secondary"
                                  onClick={() => {
                                    setOtpSent(false);
                                    setOtpCooldown(0);
                                  }}
                                >
                                  Change Number
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Post-verify state */}
                          {otpVerified && (
                            <>
                              <div className="pf-verified">✔ Mobile number verified successfully</div>
                              {/* {isExistingUser !== null && (
                                <div className={`pf-info-box ${isExistingUser ? "pf-existing" : "pf-new"}`}>
                                  {isExistingUser ? (
                                    <>
                                      <strong>Existing GOQii user found</strong>
                                      This member will be linked to the existing account.
                                    </>
                                  ) : (
                                    <>
                                      <strong>New GOQii account will be created</strong>
                                      A new account will be created for this mobile number.
                                    </>
                                  )}
                                </div>
                              )} */}
                            </>
                          )}

                          {/* Save */}
                          <div className="pf-form-actions" style={{ marginTop: 20 }}>
                            <button
                              className="pf-btn pf-btn-primary"
                              onClick={handleSaveMember}
                              disabled={!otpVerified}
                            >
                              Save Member
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Activation progress ── */}
        {showActivation && (
          <div className="pf-card pf-activation" ref={activationRef}>
            <div className="pf-activation-title">Activating Member Accounts</div>
            <p className="pf-activation-sub">
              Please wait while we create GOQii accounts and activate each purchased plan.
            </p>
            {activationApiLoading && (
              <div className="pf-loader-inline">
                <span className="pf-loader-spinner" />
                Submitting activation request...
              </div>
            )}
            <div className="pf-activation-list">
              {memberEntries.map((m, i) => (
                <div key={i} className="pf-activation-row">
                  <span>{m.name || m.memberLabel} — {m.planTitle}</span>
                  <span className={`pf-state-${activationStates[i] || "pending"}`}>
                    {activationStates[i] === "active"
                      ? "Active ✓"
                      : activationStates[i] === "progress"
                      ? "Activating ⏳"
                      : "Pending"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Completion ── */}
        {showCompletion && (
          <div className="pf-card pf-completion" ref={completionRef}>
            <div className="pf-completion-icon">✓</div>
            <div className="pf-completion-title">All Accounts Activated Successfully</div>
            <p style={{ color: "var(--ink-3)", fontSize: 15, lineHeight: 1.75, maxWidth: 520, margin: "0 auto" }}>
              All required member details have been added and all plans are now activated successfully.
            </p>
            <div className="pf-footer-actions">
              <button className="pf-btn pf-btn-primary" onClick={onReset}>
                Go to Main Page
              </button>
              <button className="pf-btn pf-btn-secondary">
                Download GOQii
              </button>
            </div>
          </div>
        )}

      </div>

      {/* ── Footer — same as main page ── */}
      <Footer />

    </div>
  );
}
